// Lazy-load the Razorpay Checkout SDK from their CDN and open checkout.
// Returns a promise that resolves with the { paymentId, orderId, signature }
// trio on success, or rejects on user cancel / failure.

declare global {
  interface Window {
    Razorpay?: new (opts: Record<string, unknown>) => {
      open: () => void;
      on: (e: string, cb: (p: unknown) => void) => void;
    };
  }
}

let loader: Promise<void> | null = null;

export function loadRazorpay(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.Razorpay) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
    document.head.appendChild(s);
  });
  return loader;
}

export interface OpenCheckoutParams {
  keyId: string;
  orderId: string;
  amountPaise: string;
  currency: string;
  name: string;
  description?: string;
  prefill?: { name?: string | null; email?: string | null; contact?: string | null };
}

export interface CheckoutResult {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export async function openCheckout(p: OpenCheckoutParams): Promise<CheckoutResult> {
  await loadRazorpay();
  return new Promise((resolve, reject) => {
    if (!window.Razorpay) return reject(new Error('Razorpay not available'));
    const rzp = new window.Razorpay({
      key: p.keyId,
      order_id: p.orderId,
      amount: p.amountPaise,
      currency: p.currency,
      name: p.name,
      description: p.description,
      prefill: {
        name: p.prefill?.name ?? undefined,
        email: p.prefill?.email ?? undefined,
        contact: p.prefill?.contact ?? undefined,
      },
      handler: (resp: {
        razorpay_order_id: string;
        razorpay_payment_id: string;
        razorpay_signature: string;
      }) =>
        resolve({
          razorpayOrderId: resp.razorpay_order_id,
          razorpayPaymentId: resp.razorpay_payment_id,
          razorpaySignature: resp.razorpay_signature,
        }),
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
    });
    rzp.on('payment.failed', (resp: unknown) =>
      reject(
        new Error(
          (resp as { error?: { description?: string } })?.error?.description ?? 'Payment failed',
        ),
      ),
    );
    rzp.open();
  });
}
